import React, { Component } from 'react';
import Highcharts from 'highcharts';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import set from 'lodash/set';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { touch_object as touchObject } from '../../js/app/react/helpers.objects';
import { isNumeric, isEmpty } from '../../js/app/react/validators';

// --------------------------------------------
//
//  DO NOT USE THIS, USE CHART.JSX INSTEAD, thanks
//
// --------------------------------------------

const { ADV } = window;

// We need to do this in order to make Highcharts available in the legacy HM codebase
// TODO: Remove in after react migration is done
if (!window.Highcharts) {
  window.Highcharts = Highcharts;
}

export const isSeriesDataTheOnlyDifference = (previousPropsArg, propsArg) => {
  const previousProps = cloneDeep(previousPropsArg);
  const props = cloneDeep(propsArg);
  if (!isEqual(props, previousProps)) {
    if (props.options.series.length !== previousProps.options.series.length) {
      return false;
    }
    [props, previousProps].forEach(prop => {
      set(prop, 'options.chart.renderTo', null);
      for (let i = 0; i < props.options.series.length; i += 1) {
        set(prop, `options.series[${i}].data`, null);
      }
    });

    return isEqual(props.options, previousProps.options);
  }
  return true;
};

export const areEventHandlersADifference = (previousPropsArg, propsArg) => {
  const EVENTS_PATH = 'options.chart.events';
  return !isEqual(get(previousPropsArg, EVENTS_PATH), get(propsArg, EVENTS_PATH));
};

export const areLimitsADifference = (previousPropsArg, propsArg) => {
  const MAX_Y_PATH = 'options.yAxis.max';
  const MIN_Y_PATH = 'options.yAxis.min';

  return (
    !isEqual(get(previousPropsArg, MAX_Y_PATH), get(propsArg, MAX_Y_PATH)) ||
    !isEqual(get(previousPropsArg, MIN_Y_PATH), get(propsArg, MIN_Y_PATH))
  );
};

export default class ReactHighcharts extends Component {
  static destroyChart(chart) {
    if (isEmpty(chart)) {
      return;
    }
    chart.destroy();
  }

  constructor(props) {
    super(props);
    this.resize = this.resize.bind(this);
    this.onContainerRef = this.onContainerRef.bind(this);
    this.createChart = this.createChart.bind(this);
  }

  UNSAFE_componentWillMount() {
    const { autoResize } = this.props;
    this.setState({
      autoResize,
    });
  }

  // When the DOM is ready, create the chart.
  componentDidMount() {
    if (this.state.autoResize) {
      requestAnimationFrame(this.resize);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { autoResize } = nextProps;
    if (!autoResize || !this.state.autoResize) {
      this.setState({
        autoResize,
      });
    }
  }

  componentDidUpdate(previousProps, previousState) {
    const { options } = this.props;
    const { renderContainer, chart, autoResize } = this.state;

    if (autoResize && !previousState.autoResize) {
      requestAnimationFrame(this.resize);
    }

    if (!previousState.renderContainer && renderContainer) {
      this.createChart();
      return;
    }

    if (isEmpty(chart)) {
      return;
    }

    if (ADV.util.shallowEqual(options, previousProps.options)) {
      return;
    }

    chart.options.custom = options.custom;

    if (areEventHandlersADifference(previousProps, this.props)) {
      this.createChart();
      return;
    }

    if (areLimitsADifference(previousProps, this.props)) {
      this.createChart();
      return;
    }
    chart.update(options);
  }

  componentWillUnmount() {
    const { chart } = this.state;
    this.cancelResize = true;
    ReactHighcharts.destroyChart(chart);
  }

  onContainerRef(renderContainer) {
    this.setState({
      renderContainer,
    });
  }

  createChart() {
    const { options, onHighchartsRef } = this.props;
    const { chart, renderContainer } = this.state;

    touchObject(options, 'chart');
    ReactHighcharts.destroyChart(chart);

    options.chart.renderTo = renderContainer;
    const highchart = new Highcharts.Chart(options);

    if (onHighchartsRef) {
      onHighchartsRef(highchart);
    }
    this.setState({
      chart: highchart,
    });
  }

  resize() {
    const { chart, renderContainer, autoResize } = this.state;
    if (this.cancelResize) {
      return;
    }
    if (autoResize) {
      if (chart) {
        const { $ } = window;
        const width = parseFloat($(renderContainer).width());
        if (isNumeric(width) && chart && chart.chartWidth !== width) {
          chart.setSize(width, undefined, false);
        }
      }
      requestAnimationFrame(this.resize);
    }
  }

  render() {
    const { container, className } = this.props;
    return <div ref={this.onContainerRef} id={container} className={className} />;
  }
}

ReactHighcharts.defaultProps = {
  autoResize: false,
  container: undefined,
  className: '',
  onHighchartsRef: null,
};

ReactHighcharts.propTypes = {
  autoResize: PropTypes.bool,
  className: PropTypes.string,
  container: PropTypes.string,
  onHighchartsRef: PropTypes.func,
  options: PropTypes.shape({
    series: PropTypes.arrayOf(
      PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    ),
  }).isRequired,
};
